import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import Layout from "./components/Layout";
import PortalApp from "./portal/PortalApp";
import StaffAppRoutes from "./staffApp/StaffAppRoutes";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import POSPage from "./pages/POSPage";
import InventoryPage from "./pages/InventoryPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import PricingPage from "./pages/PricingPage";
import DrugInteractionPage from "./pages/DrugInteractionPage";
import UsersPage from "./pages/UsersPage";
import TerminalsPage from "./pages/TerminalsPage";
import SmsLogPage from "./pages/SmsLogPage";
import ShiftHandoversPage from "./pages/ShiftHandoversPage";
import SuppliersPage from "./pages/SuppliersPage";
import SupplierDetailPage from "./pages/SupplierDetailPage";
import PurchasesPage from "./pages/PurchasesPage";
import PrescriptionScanPage from "./pages/PrescriptionScanPage";
import ReorderPage from "./pages/ReorderPage";
import NearExpiryPage from "./pages/NearExpiryPage";
import BranchRecallReportPage from "./pages/BranchRecallReportPage";
import SupplierReturnsPage from "./pages/SupplierReturnsPage";
import SalesHistoryPage from "./pages/SalesHistoryPage";
import RefillsPage from "./pages/RefillsPage";
import BulkImportPage from "./pages/BulkImportPage";
import StockAdjustmentsPage from "./pages/StockAdjustmentsPage";
import ControlledSubstanceRegistryPage from "./pages/ControlledSubstanceRegistryPage";
import ExpensesPage from "./pages/ExpensesPage";
import FixedAssetsPage from "./pages/FixedAssetsPage";
import FileStoragePage from "./pages/FileStoragePage";
import ClaudeAssistantPage from "./pages/ClaudeAssistantPage";
import FinancePage from "./pages/FinancePage";
import LedgerPage from "./pages/LedgerPage";
import HrPage from "./pages/HrPage";
import SettingsPage from "./pages/SettingsPage";
import DemandForecastPage from "./pages/DemandForecastPage";
import BranchesPage from "./pages/BranchesPage";
import BranchPerformancePage from "./pages/BranchPerformancePage";
import HelpPage from "./pages/HelpPage";
import InsuranceProvidersPage from "./pages/InsuranceProvidersPage";
import CorporateClientsPage from "./pages/CorporateClientsPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import BillingPage from "./pages/BillingPage";
import SystemPage from "./pages/SystemPage";
import ReportsPage from "./pages/ReportsPage";
import WhatsAppInboxPage from "./pages/WhatsAppInboxPage";
import CatalogPage from "./pages/CatalogPage";
import ExpiredProductsPage from "./pages/ExpiredProductsPage";
import DailySummaryPage from "./pages/DailySummaryPage";
import FraudAnalysisPage from "./pages/FraudAnalysisPage";
import StockTakePage from "./pages/StockTakePage";
import PackOperationsPage from './packTools/PackOperationsPage';
import PrinterProfilesPage from './packTools/PrinterProfilesPage';
import StockInsightsPage from './packTools/StockInsightsPage';
import SyncConflictsPage from "./pages/SyncConflictsPage";
import DuplicateDetectionPage from "./pages/DuplicateDetectionPage";
import AIUsagePage from "./pages/AIUsagePage";
import DutiAIPage from "./pages/DutiAIPage";
import PlansPage from "./pages/PlansPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionCallbackPage from "./pages/SubscriptionCallbackPage";
import PermissionsAuditPage from "./pages/PermissionsAuditPage";
import MarketingPage from "./pages/MarketingPage";
import SupplierScorecardPage from "./pages/SupplierScorecardPage";
import PurchasePlannerPage from "./pages/PurchasePlannerPage";
import StockTransfersPage from "./pages/StockTransfersPage";
import PortalInboxPage from "./pages/PortalInboxPage";
import PortalEngagementPage from "./pages/PortalEngagementPage";
import CustomerWorkspacePage from './care/CustomerWorkspacePage';
import POSRoute from './care/PortalPOSHandoff';
import WelcomePage from "./pages/WelcomePage";
import StandingPage from "./pages/StandingPage";
import PlatformConsole from "./platform/PlatformConsole";
import PlatformInvitePage from "./platform/PlatformInvitePage";
import PlatformPasswordRecovery from "./platform/PlatformPasswordRecovery";
import TryDemoPage from "./pages/TryDemoPage";
import PharmacySignupPage from "./pages/PharmacySignupPage";
import PSGHPartnersPage from "./pages/PSGHPartnersPage";
import BranchRemoteLogin from "./pages/BranchRemoteLogin";
import PrivacyRequestsPage from "./pages/PrivacyRequestsPage";
import { useAuth } from './contexts/AuthContext';
import { getStaffLandingPath } from './lib/staffLanding';

function DefaultHome() {
  const { user } = useAuth();
  return <ProtectedRoute><Navigate to={getStaffLandingPath(user)} replace /></ProtectedRoute>;
}

// Every plain staff page uses the exact same wrapping:
// <ProtectedRoute><Layout><Page /></Layout></ProtectedRoute>. Listing them as
// [path, Component] pairs instead of 50 near-identical <Route> blocks keeps
// this file's only real logic (the few routes with custom wrapping below)
// readable on its own.
const PROTECTED_PAGES = [
  ['/subscription', SubscriptionPage],
  ['/standing', StandingPage],
  ['/addons', StandingPage],
  ['/subscription/callback', SubscriptionCallbackPage],
  ['/admin/permissions-audit', PermissionsAuditPage],
  ['/marketing', MarketingPage],
  ['/suppliers/scorecard', SupplierScorecardPage],
  ['/purchase-planner', PurchasePlannerPage],
  ['/stock-transfers', StockTransfersPage],
  ['/portal-engagement', PortalEngagementPage],
  ['/dashboard', DashboardPage],
  ['/pos', POSRoute],
  ['/inventory', InventoryPage],
  ['/inventory/:productId', ProductDetailPage],
  ['/pricing', PricingPage],
  ['/drug-interactions', DrugInteractionPage],
  ['/suppliers', SuppliersPage],
  ['/suppliers/:supplierId', SupplierDetailPage],
  ['/purchases', PurchasesPage],
  ['/users', UsersPage],
  ['/terminals', TerminalsPage],
  ['/sms-log', SmsLogPage],
  ['/shift-handovers', ShiftHandoversPage],
  ['/prescriptions', PrescriptionScanPage],
  ['/reorder', ReorderPage],
  ['/near-expiry', NearExpiryPage],
  ['/recall-report', BranchRecallReportPage],
  ['/supplier-returns', SupplierReturnsPage],
  ['/demand-forecast', DemandForecastPage],
  ['/sales-history', SalesHistoryPage],
  ['/refills', RefillsPage],
  ['/bulk-import', BulkImportPage],
  ['/stock-adjustments', StockAdjustmentsPage],
  ['/controlled-registry', ControlledSubstanceRegistryPage],
  ['/ai-assistant', ClaudeAssistantPage],
  ['/finance', FinancePage],
  ['/finance/expenses', ExpensesPage],
  ['/finance/assets', FixedAssetsPage],
  ['/finance/files', FileStoragePage],
  ['/finance/ledger', LedgerPage],
  ['/finance/ledger/:tab', LedgerPage],
  ['/hr', HrPage],
  ['/hr/:tab', HrPage],
  ['/settings', SettingsPage],
  ['/branches', BranchesPage],
  ['/tenants', BranchesPage],
  ['/branch-performance', BranchPerformancePage],
  ['/help', HelpPage],
  ['/insurance-providers', InsuranceProvidersPage],
  ['/corporate-clients', CorporateClientsPage],
  ['/loyalty', LoyaltyPage],
  ['/billing', BillingPage],
  ['/system', SystemPage],
  ['/reports', ReportsPage],
  ['/whatsapp-inbox', WhatsAppInboxPage],
  ['/catalog', CatalogPage],
  ['/expired-products', ExpiredProductsPage],
  ['/daily-summary', DailySummaryPage],
  ['/fraud-analysis', FraudAnalysisPage],
  ['/stock-take', StockTakePage],
  ['/inventory/pack-counts', PackOperationsPage],
  ['/inventory/shelf-moves', PackOperationsPage],
  ['/inventory/printer-profiles', PrinterProfilesPage],
  ['/inventory/stock-insights', StockInsightsPage],
  ['/sync-conflicts', SyncConflictsPage],
  ['/duplicate-detection', DuplicateDetectionPage],
  ['/ai-usage', AIUsagePage],
  ['/duti-ai', DutiAIPage],
  ['/privacy-requests', PrivacyRequestsPage],
];

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/staff-app/*" element={<StaffAppRoutes />} />
      <Route path="/b/:slug" element={<BranchRemoteLogin />} />
      <Route path="/customers/*" element={<ProtectedRoute><Layout><CustomerWorkspacePage /></Layout></ProtectedRoute>} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/try-demo" element={<TryDemoPage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/signup/pharmacy" element={<PharmacySignupPage />} />
      <Route path="/partners/psgh" element={<PSGHPartnersPage />} />
      <Route path="/platform/*" element={<PlatformConsole />} />
      <Route path="/platform/forgot-password" element={<PlatformPasswordRecovery key="forgot" />} />
      <Route path="/platform/reset-password" element={<PlatformPasswordRecovery key="reset" reset />} />
      <Route path="/platform-invite/:token" element={<PlatformInvitePage />} />
      <Route path="/portal-inbox" element={
        <ProtectedRoute><Navigate to="/customers/conversations" replace /></ProtectedRoute>
      } />
      <Route path="/patients" element={<Navigate to="/customers/records" replace />} />
      {PROTECTED_PAGES.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={<ProtectedRoute><Layout><Page /></Layout></ProtectedRoute>}
        />
      ))}
      <Route path="/" element={<WelcomePage />} />
      <Route path="*" element={<DefaultHome />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Patient portal — fully isolated from staff auth */}
        <Route path="/portal/*" element={<PortalApp />} />
        {/* Staff / admin app */}
        <Route
          path="/*"
          element={
            <AuthProvider>
              <SettingsProvider>
                <AppRouter />
              </SettingsProvider>
            </AuthProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
